import { useEffect, useMemo, useRef, useState } from 'react';

import type { ChartData } from '../types';

/**
 * Renders an AI-generated (or hand-edited) HTML fragment.
 *
 * The fragment is untrusted code, so it runs in an iframe with
 * `sandbox="allow-scripts"` and **no** `allow-same-origin`. That combination
 * gives the frame an opaque origin: its scripts cannot reach this document,
 * our cookies, or our session token. Adding `allow-same-origin` alongside
 * `allow-scripts` would defeat the sandbox entirely — never do it here.
 *
 * A restrictive CSP inside the frame blocks network access, so a generated
 * chart cannot exfiltrate the data it is given.
 */

const THEME_VARS = [
  '--surface',
  '--text',
  '--text-secondary',
  '--border',
  '--accent',
  '--c1',
  '--c2',
  '--c3',
  '--c4',
  '--c5',
  '--c6',
] as const;

/** Copy the host's resolved theme values so the frame matches light/dark. */
const readTheme = (): string => {
  const styles = getComputedStyle(document.documentElement);
  return THEME_VARS.map((name) => `${name}: ${styles.getPropertyValue(name).trim()};`).join('\n      ');
};

interface Props {
  html: string;
  data: ChartData;
  title?: string;
}

export const CustomHtmlChart = ({ html, data, title = 'Generated chart' }: Props) => {
  const [theme, setTheme] = useState(readTheme);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Re-read the palette when the platform toggles light/dark.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setTheme(readTheme());
    media.addEventListener('change', update);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class', 'style'],
    });

    return () => {
      media.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  const srcDoc = useMemo(() => {
    // JSON.stringify escapes the data, but `</script>` inside a string would
    // still close the tag early — break the sequence so it can't.
    const payload = JSON.stringify(data).replace(/</g, '\\u003c');

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta
      http-equiv="Content-Security-Policy"
      content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data:; font-src data:;"
    />
    <style>
      :root {
        ${theme}
        color-scheme: light dark;
      }
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: transparent;
        color: var(--text);
        font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
        font-size: 13px;
      }
      #root { width: 100%; height: 100%; }
    </style>
  </head>
  <body>
    <script>window.CHART_DATA = ${payload};</script>
    <div id="root">${html}</div>
  </body>
</html>`;
  }, [html, data, theme]);

  return (
    <iframe
      ref={frameRef}
      className="custom-chart"
      title={title}
      // No allow-same-origin: the frame gets an opaque origin and cannot reach us.
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      loading="lazy"
    />
  );
};
